<template>
  <div class="sales-page">
    <APageHead title="대시보드" sub="파인애플 아카데미 운영 현황을 한눈에 확인하세요." />

    <!-- 환율 티커 -->
    <ACard body-flush class="sales-page__ticker-card">
      <div class="sales-page__ticker">
        <div class="sales-page__ticker-label">
          <i class="material-icons-round">currency_exchange</i>
          <span>실시간 환율</span>
        </div>
        <div class="sales-page__ticker-wrap">
          <div v-if="exchangeRateLoading" class="sales-page__ticker-loading">
            <i class="material-icons-round sales-page__ticker-spin">autorenew</i> 로딩중...
          </div>
          <!--
            흐르는 시간을 통화 수에 비례시킵니다. 30초 고정이면 자동 등록으로 통화가 늘어날수록
            같은 시간에 더 긴 띠를 지나가야 해서 그만큼 빨라지고, 결국 읽을 수 없게 됩니다(docs/대시보드_통계_API.md '통화 목록 자동 등록의 영향').
          -->
          <div
            v-else-if="exchangeRateList.length > 0"
            class="sales-page__ticker-track"
            :style="{ animationDuration: tickerDuration }"
          >
            <template v-for="repeat in 2" :key="repeat">
              <span class="sales-page__ticker-base">1 USD =</span>
              <template v-for="item in exchangeRateList" :key="repeat + '-' + item.code">
                <span class="sales-page__ticker-item">
                  <span class="sales-page__ticker-code">{{ item.code }}</span>
                  <span class="sales-page__ticker-value">{{ item.icon }}{{ item.displayRate }}</span>
                </span>
                <span class="sales-page__ticker-sep">|</span>
              </template>
            </template>
          </div>
          <div v-else class="sales-page__ticker-loading">환율 정보를 불러올 수 없습니다.</div>
        </div>
        <div v-if="exchangeRateUpdatedAt && exchangeRateList.length > 0" class="sales-page__ticker-time">
          <i class="material-icons-round">schedule</i>
          {{ exchangeRateUpdatedAt }}
        </div>
      </div>
    </ACard>

    <!-- KPI -->
    <div class="sales-page__stats">
      <ACard>
        <AStat
          label="총 판매액"
          :value="formatCurrency(stats.totalSales)"
          :delta="(stats.salesGrowth >= 0 ? '+' : '') + stats.salesGrowth + '%'"
          :delta-kind="stats.salesGrowth >= 0 ? 'ok' : 'err'"
        />
      </ACard>
      <ACard>
        <AStat
          label="총 주문 건수"
          :value="formatNumber(stats.totalOrders)"
          unit="건"
          :delta="(stats.ordersGrowth >= 0 ? '+' : '') + stats.ordersGrowth + '%'"
          :delta-kind="stats.ordersGrowth >= 0 ? 'ok' : 'err'"
          :delta-sub="`이번달 ${formatNumber(stats.monthOrders)} / 지난달 ${formatNumber(stats.lastMonthOrders)}`"
        />
      </ACard>
      <ACard>
        <AStat
          label="학습자 수"
          :value="formatNumber(stats.totalUsers)"
          unit="명"
          :delta="'+' + formatNumber(stats.newUsersThisMonth)"
          delta-kind="ok"
          delta-sub="이번달 신규"
        />
      </ACard>
    </div>

    <!--
      매출 0 일 때 띄우던 "등록되지 않은 통화 코드" 경고를 걷어냈습니다(docs/대시보드_통계_API.md '통화 목록 자동 등록의 영향').

      그 경고는 2026-09-11 IDR 사고 — 통화 테이블에 없는 코드가 섞여 통계가 500 이 나고 이 화면이
      오류를 삼켜 매출 0 을 보여준 일 — 을 설명하려고 둔 것이었습니다. 이제 서버가 처음 보는 통화를
      결제·환불 시점에 자동 등록하고 매일 06시에 누락분을 다시 채우므로 그 원인 자체가 사라졌고,
      운영자가 화폐를 등록할 화면도 없어 안내가 가리키는 다음 동작이 없습니다.
      남겨 두면 진짜로 판매가 없던 날마다 없는 장애를 알리게 됩니다.
    -->

    <!-- 차트 섹션 -->
    <div class="sales-page__charts">
      <ACard class="sales-page__charts-pie">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">pie_chart</i>
            국가별 판매 비율
          </span>
        </template>
        <div class="sales-page__pie-row">
          <div class="sales-page__pie-canvas">
            <PieChart
              v-if="chartDataLoaded && countrySalesChart.labels.length > 0"
              id="country-sales-pie"
              :key="'pie-' + countrySalesChart.labels.join('-')"
              :chart="countrySalesChart"
            />
            <div v-else class="sales-page__chart-empty">
              <i class="material-icons-round">pie_chart</i>
              <p>데이터 없음</p>
            </div>
          </div>
          <ul class="sales-page__pie-legend">
            <li v-for="(item, idx) in countrySales.slice(0, 4)" :key="idx">
              <span :class="['sales-page__pie-dot', `sales-page__pie-dot--${idx}`]"></span>
              <span>{{ item.country }}</span>
            </li>
          </ul>
        </div>
      </ACard>

      <ACard class="sales-page__charts-line">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">show_chart</i>
            월별 매출 추이
          </span>
        </template>
        <template #action>
          <div class="sales-page__line-legend">
            <span><span class="sales-page__pie-dot sales-page__pie-dot--0"></span>매출액</span>
            <span><span class="sales-page__pie-dot sales-page__pie-dot--dark"></span>주문 건수</span>
          </div>
        </template>
        <div class="sales-page__line-canvas">
          <DefaultLineChart
            v-if="chartDataLoaded && monthlySalesChart.labels.length > 0"
            id="monthly-sales-line"
            :key="'line-' + monthlySalesChart.labels.join('-')"
            :chart="monthlySalesChart"
          />
          <div v-else class="sales-page__chart-empty">
            <i class="material-icons-round">show_chart</i>
            <p>월별 매출 데이터를 불러오는 중...</p>
          </div>
        </div>
      </ACard>
    </div>

    <!-- 서비스 현황 + 국가별 판매 -->
    <div class="sales-page__row">
      <ACard class="sales-page__row-main">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">apps</i>
            서비스 현황
          </span>
        </template>
        <div class="sales-page__services">
          <div class="sales-page__service">
            <APill kind="dark">{{ stats.totalTextbooks }}권</APill>
            <span>서비스 도서</span>
          </div>
          <div class="sales-page__service">
            <APill kind="ok" dot>{{ stats.totalClasses }}개</APill>
            <span>운영 클래스</span>
          </div>
          <div class="sales-page__service">
            <APill kind="warn" dot>{{ stats.totalCoupons }}장</APill>
            <span>발급 쿠폰</span>
          </div>
          <div class="sales-page__service">
            <APill kind="info" dot>{{ stats.pendingSettlementCount }}건</APill>
            <span>정산 대기</span>
          </div>
        </div>
      </ACard>

      <ACard class="sales-page__row-side">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">public</i>
            국가별 판매
          </span>
        </template>
        <ul class="sales-page__countries">
          <li v-for="(item, idx) in countrySales" :key="idx" class="sales-page__country">
            <img v-if="item.flag" :src="item.flag" alt="" class="sales-page__country-flag" />
            <i v-else class="material-icons-round sales-page__country-flag-fallback">public</i>
            <div class="sales-page__country-info">
              <small>국가</small>
              <strong>{{ item.country }}</strong>
            </div>
            <div class="sales-page__country-num">
              <small>판매</small>
              <strong>{{ item.sales }}건</strong>
            </div>
            <div class="sales-page__country-num">
              <small>비율</small>
              <strong>{{ item.percentage }}</strong>
            </div>
          </li>
        </ul>
      </ACard>
    </div>

    <!-- 인기 도서 Top 5 -->
    <ACard>
      <template #title>
        <span class="sales-page__sec-title">
          <i class="material-icons-round sales-page__sec-icon">menu_book</i>
          인기 도서 Top 5
        </span>
      </template>
      <!-- 판매량과 매출액의 집계 기준이 다릅니다. 나눠 보면 정가보다 커질 수 있어 먼저 밝혀 둡니다. -->
      <template #sub>
        판매량은 도서 신규 구매만 집계합니다. 매출액은 기능 업그레이드 결제를 포함하므로
        매출액 ÷ 판매량이 정가보다 클 수 있습니다.
      </template>
      <AEmpty v-if="popularBooks.length === 0" title="인기 도서 데이터가 없어요" size="sm" />
      <ATable v-else :columns="bookColumns" :rows="popularBooks" row-key="title">
        <template #cell:book="{ row }">
          <div class="sales-page__book">
            <div class="sales-page__book-cover">
              <img :src="row.coverImage" alt="" @error="onCoverError" />
            </div>
            <div class="sales-page__book-text">
              <div class="sales-page__book-title">{{ row.title }}</div>
              <small>{{ row.publisher }}</small>
            </div>
          </div>
        </template>
        <template #cell:authors="{ row }"><strong>{{ row.authors }}</strong></template>
        <template #cell:sales="{ row }"><span class="sales-page__mono">{{ row.salesCount }}건</span></template>
        <template #cell:revenue="{ row }"><span class="sales-page__mono">{{ row.revenue }}</span></template>
      </ATable>
    </ACard>

    <!-- 최근 주문 + 정산 현황 -->
    <div class="sales-page__row">
      <ACard class="sales-page__row-main">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">receipt_long</i>
            최근 주문 내역
          </span>
        </template>
        <template #sub>최근 5건 주문</template>
        <AEmpty v-if="recentOrders.length === 0" title="최근 주문 내역이 없어요" size="sm" />
        <ATable v-else :columns="orderColumns" :rows="recentOrders" :row-key="'paymentIdx'">
          <template #cell:user="{ row }">
            <div>
              <div class="sales-page__order-name">{{ row.userName }}</div>
              <small class="sales-page__order-email">{{ row.email }}</small>
            </div>
          </template>
          <template #cell:textbook="{ row }">
            <div>
              <div class="sales-page__order-title">{{ row.textbookTitle }}</div>
              <small class="sales-page__order-method">{{ getPaymentMethodLabel(row.paymentMethod) }}</small>
            </div>
          </template>
          <template #cell:amount="{ row }">
            <span class="sales-page__mono">{{ formatCurrencyWithCode(row.amount, row.currencyCode) }}</span>
          </template>
          <template #cell:date="{ row }">
            <span class="sales-page__mono">{{ formatDate(row.orderDate) }}</span>
          </template>
        </ATable>
      </ACard>

      <ACard class="sales-page__row-side">
        <template #title>
          <span class="sales-page__sec-title">
            <i class="material-icons-round sales-page__sec-icon">payments</i>
            정산 현황
          </span>
        </template>
        <template #sub>{{ currentMonth }} 정산</template>

        <div class="sales-page__settle-grid">
          <div class="sales-page__settle sales-page__settle--ok">
            <small>정산 완료</small>
            <strong>{{ formatCurrency(stats.completedSettlement) }}</strong>
          </div>
          <div class="sales-page__settle sales-page__settle--warn">
            <small>정산 대기</small>
            <strong>{{ formatCurrency(stats.pendingSettlement) }}</strong>
          </div>
          <div class="sales-page__settle sales-page__settle--info">
            <small>서비스 도서</small>
            <strong>{{ formatNumber(stats.totalTextbooks) }}권</strong>
          </div>
          <div class="sales-page__settle sales-page__settle--dark">
            <small>운영 클래스</small>
            <strong>{{ formatNumber(stats.totalClasses) }}개</strong>
          </div>
        </div>
      </ACard>
    </div>
  </div>
</template>

<script>
import PieChart from "@/examples/Charts/PieChart.vue";
import DefaultLineChart from "@/examples/Charts/DefaultLineChart.vue";

import US from "@/assets/img/icons/flags/US.png";
import DE from "@/assets/img/icons/flags/DE.png";
import GB from "@/assets/img/icons/flags/GB.png";
import BR from "@/assets/img/icons/flags/BR.png";
import AU from "@/assets/img/icons/flags/AU.png";

import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import { COVER_FALLBACK, onCoverError } from "@/utils/homeSection";

export default {
  name: "Sales",
  components: { PieChart, DefaultLineChart, APageHead, ACard, AStat, APill, ATable, AEmpty },
  data() {
    return {
      US, DE, GB, BR, AU,
      stats: {
        totalSales: 0, monthSales: 0, lastMonthSales: 0,
        totalOrders: 0, monthOrders: 0, lastMonthOrders: 0,
        salesGrowth: 0, ordersGrowth: 0,
        totalUsers: 0, newUsersThisMonth: 0, activeUsers: 0,
        totalTextbooks: 0, totalClasses: 0,
        pendingSettlement: 0, completedSettlement: 0,
        pendingSettlementCount: 0, completedSettlementCount: 0,
        totalCoupons: 0, usedCoupons: 0, activeCoupons: 0,
      },
      exchangeRateList: [],
      exchangeRateUpdatedAt: null,
      exchangeRateLoading: false,
      lastUpdated: "",
      currentPeriod: "",
      currentMonth: "",
      countrySales: [],
      popularBooks: [],
      recentOrders: [],
      monthlySalesChart: { labels: [], datasets: [{ label: "매출액", data: [] }, { label: "주문 건수", data: [] }] },
      countrySalesChart: { labels: [], datasets: { label: "판매 건수", data: [] } },
      chartDataLoaded: false,
      bookColumns: [
        { key: "book", label: "도서명", wrap: true },
        { key: "authors", label: "저자", width: 240 },
        { key: "sales", label: "도서 판매(신규)", align: "center", width: 100 },
        { key: "revenue", label: "매출액", align: "right", width: 140 },
      ],
      orderColumns: [
        { key: "user", label: "주문자", wrap: true },
        { key: "textbook", label: "도서 / 결제수단", wrap: true },
        { key: "amount", label: "금액", align: "right", width: 120 },
        { key: "date", label: "일시", align: "right", width: 130 },
      ],
    };
  },
  computed: {
    // 통화 1개당 5초. 종전 감각(통화 6개 · 30초)을 그대로 두면서, 목록이 늘어나도 속도가 유지됩니다.
    // 하한 20초는 통화가 한두 개만 잡혔을 때 띠가 너무 느리게 기어가지 않도록 둡니다.
    tickerDuration() {
      return `${Math.max(20, this.exchangeRateList.length * 5)}s`;
    },
  },
  async mounted() {
    this.initializePeriod();
    await Promise.all([this.loadDashboardData(), this.loadMonthlySales(), this.loadExchangeRates()]);
  },
  methods: {
    async loadExchangeRates() {
      this.exchangeRateLoading = true;
      try {
        const res = await this.$axios({ method: "get", url: "/common/currencies" });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.exchangeRateList = res.data
            .filter((c) => c.code !== "USD")
            .map((c) => ({
              code: c.code,
              // 자동 등록된 통화는 기호를 모르면 icon 에 통화 코드가 그대로 들어옵니다(docs/대시보드_통계_API.md '통화 목록 자동 등록의 영향').
              // 티커는 코드를 이미 앞에 찍으므로 그대로 두면 "SEK SEK10.5" 가 됩니다.
              icon: c.icon === c.code ? "" : c.icon,
              displayRate: c.rate_of_receive >= 100
                ? c.rate_of_receive.toLocaleString("ko-KR", { maximumFractionDigits: 0 })
                : c.rate_of_receive.toLocaleString("ko-KR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            }));
          this.exchangeRateUpdatedAt = new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
        }
      } catch (e) {
        console.error("환율 정보 로드 실패:", e);
      } finally {
        this.exchangeRateLoading = false;
      }
    },
    initializePeriod() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      this.currentPeriod = `${start.getMonth() + 1}월 1일 - ${now.getMonth() + 1}월 ${now.getDate()}일`;
      this.currentMonth = `${now.getFullYear()}년 ${now.getMonth() + 1}월`;
    },
    async loadDashboardData() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/dashboard/stats",
          params: { period: "month", currency: "KRW" },
          loadingBar: true,
        });
        if (res?.data?.success) this.processDashboardData(res.data.data);
        else this.setDefaultData();
      } catch (error) {
        console.error("대시보드 데이터 로드 실패:", error);
        this.handleApiError(error);
        this.setDefaultData();
      }
    },
    async loadMonthlySales() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/dashboard/monthly-sales",
          params: { months: 6, currency: "KRW" },
        });
        if (res?.data?.success) this.processMonthlySalesData(res.data.data);
      } catch (error) {
        console.error("월별 매출 데이터 로드 실패:", error);
      }
    },
    processMonthlySalesData(data) {
      if (data.monthlySales && data.monthlySales.length > 0) {
        this.monthlySalesChart = {
          labels: data.monthlySales.map((m) => m.label),
          datasets: [
            { label: "매출액 (만원)", data: data.monthlySales.map((m) => Math.round(m.totalAmount / 10000)) },
            { label: "주문 건수", data: data.monthlySales.map((m) => m.orderCount) },
          ],
        };
        this.chartDataLoaded = true;
      }
    },
    processDashboardData(data) {
      if (data.sales) Object.assign(this.stats, {
        totalSales: data.sales.totalAmount || 0,
        monthSales: data.sales.monthAmount || 0,
        lastMonthSales: data.sales.lastMonthAmount || 0,
        totalOrders: data.sales.totalOrders || 0,
        monthOrders: data.sales.monthOrders || 0,
        lastMonthOrders: data.sales.lastMonthOrders || 0,
        salesGrowth: data.sales.growthRate || 0,
        ordersGrowth: data.sales.ordersGrowthRate || 0,
      });
      if (data.users) Object.assign(this.stats, {
        totalUsers: data.users.totalCount || 0,
        newUsersThisMonth: data.users.newThisMonth || 0,
        activeUsers: data.users.activeCount || 0,
      });
      if (data.content) Object.assign(this.stats, {
        totalTextbooks: data.content.textbookCount || 0,
        totalClasses: data.content.classCount || 0,
      });
      if (data.settlement) Object.assign(this.stats, {
        pendingSettlement: data.settlement.pendingAmount || 0,
        completedSettlement: data.settlement.completedAmount || 0,
        pendingSettlementCount: data.settlement.pendingCount || 0,
        completedSettlementCount: data.settlement.completedCount || 0,
      });
      if (data.coupons) Object.assign(this.stats, {
        totalCoupons: data.coupons.totalCount || 0,
        usedCoupons: data.coupons.usedCount || 0,
        activeCoupons: data.coupons.activeCount || 0,
      });

      if (data.countrySales && data.countrySales.length > 0) {
        const top = data.countrySales.slice(0, 4);
        this.countrySales = data.countrySales.slice(0, 5).map((c) => ({
          country: c.country || "미지정",
          sales: c.orderCount || 0,
          percentage: (c.percentage || 0) + "%",
          flag: this.getCountryFlag(c.countryCode),
        }));
        this.countrySalesChart = {
          labels: top.map((c) => c.country || "미지정"),
          datasets: { label: "판매 건수", data: top.map((c) => c.orderCount || 0) },
        };
        this.chartDataLoaded = true;
      } else this.setDefaultCountrySales();

      if (data.popularBooks && data.popularBooks.length > 0) {
        this.popularBooks = data.popularBooks.slice(0, 5).map((b) => ({
          title: b.title,
          authors: Array.isArray(b.authors) ? b.authors.join(", ") : (b.authors || "-"),
          publisher: b.publisher || "-",
          salesCount: this.formatNumber(b.salesCount),
          revenue: this.formatCurrency(b.revenue),
          // 표지를 아직 올리지 않은 도서는 coverImage 가 null 입니다(docs/대시보드_통계_API.md popularBooks).
          // 종전 폴백 /img/default-cover.png 는 public/ 에 없어 깨진 이미지가 떴습니다.
          coverImage: b.coverImage || COVER_FALLBACK,
        }));
      } else this.popularBooks = [];

      if (data.recentOrders && data.recentOrders.length > 0) {
        this.recentOrders = data.recentOrders.map((o) => ({
          paymentIdx: o.paymentIdx,
          userName: o.userName || "Unknown",
          email: o.email || "",
          textbookTitle: o.textbookTitle || "N/A",
          amount: o.amount || 0,
          currencyCode: o.currencyCode || "KRW",
          paymentMethod: o.paymentMethod || "",
          orderDate: o.orderDate,
        }));
      } else this.recentOrders = [];

      if (data.meta) this.lastUpdated = data.meta.generatedAt || "";
    },
    handleApiError(error) {
      if (error.response?.status === 401) this.$router.push("/authentication/signin");
      else if (error.response?.status === 403) this.$notifyVue({ type: "danger", title: "접근 권한이 없습니다." });
      else this.$notifyVue({ type: "warning", title: "데이터를 불러오는데 실패했습니다." });
    },
    setDefaultData() { this.setDefaultCountrySales(); this.popularBooks = []; this.recentOrders = []; },
    setDefaultCountrySales() {
      this.countrySales = [
        { country: "United States", sales: 0, percentage: "0%", flag: this.US },
        { country: "Germany", sales: 0, percentage: "0%", flag: this.DE },
        { country: "Great Britain", sales: 0, percentage: "0%", flag: this.GB },
        { country: "Brasil", sales: 0, percentage: "0%", flag: this.BR },
        { country: "Australia", sales: 0, percentage: "0%", flag: this.AU },
      ];
    },
    getCountryFlag(code) {
      if (!code || code === "Unknown") return null;
      return { US: this.US, DE: this.DE, GB: this.GB, BR: this.BR, AU: this.AU }[code] || null;
    },
    onCoverError,
    formatCurrency(v) { if (!v) return "₩0"; return "₩" + Number(v).toLocaleString("ko-KR"); },
    formatNumber(v) { if (!v) return "0"; return Number(v).toLocaleString("ko-KR"); },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    },
    getPaymentMethodLabel(code) {
      return { A: "Apple Pay", G: "Google Pay", S: "Stripe", T: "토스페이먼츠", L: "Lemonsqueezy" }[code] || code || "";
    },
    formatCurrencyWithCode(v, code) {
      if (!v) return "₩0";
      const symbol = { KRW: "₩", USD: "$", EUR: "€", JPY: "¥", CNY: "¥" }[code] || "₩";
      return symbol + Number(v).toLocaleString("ko-KR");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sales-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  // ── 환율 티커 ──
  &__ticker-card { overflow: hidden; }

  &__ticker {
    display: flex;
    align-items: stretch;
    height: 40px;
  }

  &__ticker-label {
    background: $p-ink;
    color: $p-point;
    padding: 0 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
    letter-spacing: 0.04em;

    > i { font-size: 14px !important; }
  }

  &__ticker-wrap {
    flex: 1;
    overflow: hidden;
    position: relative;
    background: $p-panel-alt;
  }

  &__ticker-loading {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 14px;
    font-size: 11px;
    color: $p-ink-mid;
  }

  &__ticker-spin {
    font-size: 14px !important;
    margin-right: 4px;
    animation: sales-spin 1s linear infinite;
  }

  &__ticker-track {
    display: flex;
    align-items: center;
    white-space: nowrap;
    height: 100%;
    animation: sales-ticker-scroll 30s linear infinite;

    &:hover { animation-play-state: paused; }
  }

  &__ticker-base {
    font-size: 10px;
    color: $p-ink-soft;
    font-weight: 700;
    margin-right: 14px;
    padding-left: 20px;
    letter-spacing: 0.04em;
  }

  &__ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-right: 8px;
  }

  &__ticker-sep {
    color: $p-line-hard;
    margin-right: 8px;
    font-size: 11px;
  }

  &__ticker-code {
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 800;
    color: $p-ink-soft;
  }

  &__ticker-value {
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
  }

  &__ticker-time {
    border-left: 1px solid $p-line;
    padding: 0 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;
    flex-shrink: 0;

    > i { font-size: 13px !important; }
  }

  // ── KPI ──
  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  // ── 차트 ──
  &__charts {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 16px;

    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__pie-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: center;
  }

  &__pie-canvas {
    position: relative;
    width: 100%;
    height: 220px;

    :deep(canvas) {
      width: 100% !important;
      height: 100% !important;
    }
  }

  &__pie-legend {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    > li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: $p-ink2;
    }
  }

  &__pie-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;

    // PieChart.vue 컬러 팔레트와 동일 순서 매핑
    &--0 { background: #C98A0A; }  // point-deep
    &--1 { background: #FFC93C; }  // point
    &--2 { background: #2F8F4F; }  // ok
    &--3 { background: #3372C2; }  // info
    &--dark { background: $p-ink; }
  }

  &__line-legend {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: $p-ink2;

    > span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
  }

  &__line-canvas {
    position: relative;
    width: 100%;
    height: 280px;

    :deep(canvas) {
      width: 100% !important;
      height: 100% !important;
    }
  }

  &__chart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 100%;
    color: $p-ink-soft;

    > i { font-size: 32px !important; opacity: 0.5; }
    p { margin: 0; font-size: 12px; }
  }

  // ── 서비스 + 국가별 ──
  &__row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 16px;

    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__row-main { min-width: 0; }
  &__row-side { min-width: 0; }

  &__services {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 480px) { grid-template-columns: 1fr; }
  }

  &__service {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: $p-panel-alt;
    border-radius: $p-radius-md;
    border: 1px solid $p-line-soft;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__countries {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__country {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__country-flag {
    width: 24px;
    height: 16px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
  }

  &__country-flag-fallback {
    font-size: 22px !important;
    color: $p-ink-soft;
    flex-shrink: 0;
  }

  &__country-info {
    flex: 1;
    min-width: 0;

    > small {
      display: block;
      font-size: 9px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: $p-ink-soft;
      font-weight: 800;
    }
    > strong {
      font-size: 12px;
      color: $p-ink;
    }
  }

  &__country-num {
    text-align: center;

    > small {
      display: block;
      font-size: 9px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: $p-ink-soft;
      font-weight: 800;
    }
    > strong {
      font-family: $p-font-mono;
      font-size: 12px;
      color: $p-point-deep;
    }
  }

  // ── 도서 ──
  &__book {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__book-cover {
    width: 40px;
    height: 56px;
    border-radius: $p-radius-sm;
    overflow: hidden;
    box-shadow: $p-shadow-sm;
    flex-shrink: 0;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__book-text { min-width: 0; }

  &__book-title {
    font-weight: 700;
    color: $p-ink;
    font-size: 12px;
  }

  // ── 주문 ──
  &__order-name { font-weight: 700; color: $p-ink; }
  &__order-email { color: $p-ink-soft; font-family: $p-font-mono; font-size: 10px; }
  &__order-title { font-weight: 700; color: $p-ink2; font-size: 12px; }
  &__order-method { color: $p-ink-soft; font-size: 10px; }

  &__mono {
    font-family: $p-font-mono;
    font-weight: 700;
    color: $p-ink;
  }

  // ── 정산 그리드 ──
  &__settle-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  &__settle {
    padding: 12px 14px;
    border-radius: $p-radius-md;
    border: 1px solid;
    display: flex;
    flex-direction: column;
    gap: 4px;

    > small {
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 800;
      opacity: 0.8;
    }
    > strong {
      font-family: $p-font-mono;
      font-size: 16px;
      font-weight: 800;
    }

    &--ok {
      background: $p-ok-soft;
      border-color: lighten($p-ok, 32%);
      color: $p-ok;
    }
    &--warn {
      background: $p-warn-soft;
      border-color: lighten($p-warn, 28%);
      color: $p-warn;
    }
    &--info {
      background: $p-info-soft;
      border-color: lighten($p-info, 28%);
      color: $p-info;
    }
    &--dark {
      background: $p-ink;
      border-color: $p-ink;
      color: $p-point;
    }
  }
}

@keyframes sales-ticker-scroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

@keyframes sales-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
